import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { stcBinCategoryService, StcBinCategoryDto } from '../../../../../services/stcBinCategoryService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { BinCategoryBulkUpdateModal } from './BinCategoryBulkUpdateModal';
import { BinCategoryCreateModal } from './BinCategoryCreateModal';

interface BinCategoryDataTableProps {
    refreshTrigger?: number;
}

export const BinCategoryDataTable: React.FC<BinCategoryDataTableProps> = ({
    refreshTrigger
}) => {
    const { user } = useAuth();
    const companyNo = user?.companyNo || '';

    // Core data state
    const [data, setData] = useState<StcBinCategoryDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    // Modal state
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Inline editing
    const inlineEditConfig = useInlineEdit<StcBinCategoryDto>({
        getRowId: (row) => row.id,
        onSave: async (_originalRow, editedData) => {
            await stcBinCategoryService.update(
                companyNo,
                editedData.stcBinCatNo as number,
                {
                    lng_1: (editedData.lng_1 as string) || '',
                    lng_2: (editedData.lng_2 as string) || '',
                    lng_3: (editedData.lng_3 as string) || ''
                }
            );
        },
        onSaveSuccess: () => loadData(),
        onSaveError: (err) => setError(err.message || 'Feil ved lagring')
    });

    // Load data on mount and refresh
    useEffect(() => {
        loadData();
    }, [companyNo]);

    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            loadData();
        }
    }, [refreshTrigger]);

    const loadData = useCallback(async () => {
        if (!companyNo) return;

        setIsLoading(true);
        setError(null);

        try {
            let categories: StcBinCategoryDto[] = [];
            try {
                categories = await stcBinCategoryService.getCurrent();
            } catch {
                try {
                    categories = await stcBinCategoryService.getAll();
                } catch {
                    categories = [];
                }
            }
            setData(categories);
        } catch (err: any) {
            console.error('Error loading bin categories:', err);
            setError('Kunne ikke laste lokasjonskategorier. Prøv igjen.');
        } finally {
            setIsLoading(false);
        }
    }, [companyNo]);

    // Column factory
    const columnFactory = createColumnFactory<StcBinCategoryDto>();

    // Format date for display
    const formatDate = (value: any): string => {
        if (!value) return '-';
        try {
            const date = new Date(value);
            return date.toLocaleDateString('nb-NO', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return String(value);
        }
    };

    // Column definitions
    const columns: ColumnDefinition<StcBinCategoryDto>[] = useMemo(() => [
        columnFactory.createNumberColumn('stcBinCatNo', 'stcBinCatNo', 'Kategori nr.', {
            minWidth: 120,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('lng_1', 'lng_1', 'Beskrivelse', {
            minWidth: 200,
            sortable: true,
            filterType: 'both',
            editable: true
        }),
        columnFactory.createTextColumn('lng_2', 'lng_2', 'Språk 2', {
            minWidth: 200,
            sortable: true,
            filterType: 'both',
            visible: false,
            editable: true
        }),
        columnFactory.createTextColumn('lng_3', 'lng_3', 'Språk 3', {
            minWidth: 200,
            sortable: true,
            filterType: 'both',
            visible: false,
            editable: true
        }),
        columnFactory.createTextColumn('createdBy', 'createdBy', 'Opprettet av', {
            minWidth: 130,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createDateColumn('created', 'created', 'Opprettet', {
            minWidth: 160,
            sortable: true,
            filterType: 'none',
            formatter: formatDate
        }),
        columnFactory.createTextColumn('updatedBy', 'updatedBy', 'Endret av', {
            minWidth: 130,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createDateColumn('lastUpdate', 'lastUpdate', 'Sist endret', {
            minWidth: 160,
            sortable: true,
            filterType: 'none',
            formatter: formatDate
        })
    ], []);

    // Bulk edit handler
    const handleBulkEdit = useCallback(() => {
        if (selectedIds.length === 0) {
            return;
        }
        setShowBulkEditModal(true);
    }, [selectedIds.length]);

    // Handle modal success
    const handleUpdateSuccess = async () => {
        await loadData();
        setSelectedIds([]);
        setShowBulkEditModal(false);
    };

    const handleDeleteSuccess = async () => {
        await loadData();
        setSelectedIds([]);
        setShowBulkEditModal(false);
    };

    const handleCreateSuccess = async () => {
        await loadData();
        setShowCreateModal(false);
    };

    // Table actions
    const tableActions: TableAction<StcBinCategoryDto>[] = useMemo(() => [
        {
            id: 'create',
            label: 'Ny lokasjonskategori',
            icon: Plus,
            onClick: () => setShowCreateModal(true),
            variant: 'primary'
        },
        {
            id: 'bulk-edit',
            label: `Vedlikehold (${selectedIds.length})`,
            icon: Edit,
            onClick: () => handleBulkEdit(),
            disabled: () => selectedIds.length === 0,
            variant: 'primary'
        }
    ], [selectedIds.length, handleBulkEdit]);

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-50">
            <div className="flex-1 min-h-0">
                <WarehouseTable
                    data={data}
                    columns={columns}
                    config={{
                        showPagination: true,
                        showColumnConfig: true,
                        showGlobalSearch: true,
                        showFilters: true,
                        showExport: true,
                        showPrint: true,
                        showSummary: false,
                        defaultPageSize: 50,
                        allowColumnReorder: true,
                        allowColumnResize: true,
                        stickyHeader: true,
                        compactMode: true,
                        persistenceKey: 'bin-categories-table',
                        allowRowSelection: {
                            mode: 'multi',
                            selectAllMode: 'all-data'
                        }
                    }}
                    selectedRows={selectedIds}
                    onSelectionChange={(ids) => {
                        setSelectedIds(ids as number[]);
                    }}
                    getRowId={(item) => item.id}
                    tableActions={tableActions}
                    inlineEditConfig={inlineEditConfig}
                    loading={isLoading}
                    error={error || undefined}
                />
            </div>

            {/* Bulk Update Modal */}
            {showBulkEditModal && selectedIds.length > 0 && (
                <BinCategoryBulkUpdateModal
                    isOpen={showBulkEditModal}
                    onClose={() => setShowBulkEditModal(false)}
                    selectedIds={selectedIds}
                    data={data}
                    onUpdateSuccess={handleUpdateSuccess}
                    onDeleteSuccess={handleDeleteSuccess}
                />
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <BinCategoryCreateModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={handleCreateSuccess}
                />
            )}
        </div>
    );
};
